import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { MessageService } from 'src/app/core/services/message.service';
import { environment } from 'src/environments/environment';
import { BehaviorSubject, ReplaySubject, catchError, Observable, map } from 'rxjs';

import { PageDonnee } from 'src/app/shared/interfaces/page-donnee';

@Injectable({
  providedIn: 'root'
})
export class UtilisateurService {

  constructor(
    private messageService: MessageService
    ,private http: HttpClient
    ) { }

  private donneesSubject = new BehaviorSubject<any[]>([]);
  public donnees$ = this.donneesSubject.asObservable();

  private chargementSubject = new BehaviorSubject<boolean>(false);
  public chargement$ = this.chargementSubject.asObservable();

  private indexPageSubject = new ReplaySubject<number>(0);
  public indexPage$ = this.indexPageSubject.asObservable();

  private taillePageSubject = new ReplaySubject<number>(10);
  public taillePage$ = this.taillePageSubject.asObservable();

  private totalElementsDonneesSubject = new BehaviorSubject<number>(0);
  public totalElementsDonnees$ = this.totalElementsDonneesSubject.asObservable();

    articlesPanier : any = [];

    montantApayerNet: number = 0;

  supprimerUtilisateur(idUtilisateur: number) : any  {
    return this.supprimerUtilisateurHttp(idUtilisateur).pipe(
      catchError(this.messageService.gererErreur)
    );
  }

  supprimerUtilisateurHttp(idUtilisateur: number) {
    const url = `${environment.apiurl}users/${idUtilisateur}`;
    return this.http.delete(url);
  }

  obtenirUtilisateurParUserId(idUtilisateur:number): Observable <Object>{
    const url = `${environment.apiurl}` + 'utilisateurClient/getUtilisateursClientByuserId/?userId='+idUtilisateur;
    return this.http.get(url).pipe(
      catchError(this.messageService.gererErreur)
    );
  }

  obtenirUtilisateurParId(id: number): Observable<any> {
    const url = `${environment.apiurl}users/${id}`;
    return this.http.get<any>(url).pipe(
      catchError(this.messageService.gererErreur)
    );
  }

  enregistrerUtilisateur(utilisateur: any): Observable<any> {
    const url = `${environment.apiurl}users`;
    return this.http.post<any>(url, utilisateur).pipe(
      catchError(this.messageService.gererErreur)
    );
  }

  modifierUtilisateur(utilisateur: any): Observable<any> {
    const url = `${environment.apiurl}users/${utilisateur.id}`;
    const { id, ...donneesUtilisateur } = utilisateur;
    return this.http.put<any>(url, donneesUtilisateur).pipe(
      catchError(this.messageService.gererErreur)
    );
  }

  obtenirUtilisateursActifs(page: number, taille: number) {
    const url = `${environment.apiurl}users`;
    this.http.get<any>(url).subscribe({
      next: (donnees) => {
        const list = Array.isArray(donnees) ? donnees : (donnees?.content ?? []);
        this.donneesSubject.next(list);
        this.chargementSubject.next(false);
        this.indexPageSubject.next(Array.isArray(donnees) ? 0 : (donnees?.page ?? 0));
        this.totalElementsDonneesSubject.next(
          Array.isArray(donnees) ? list.length : ((donnees?.nombreDonnee ?? 0) * (donnees?.totalPage ?? 1))
        );
      },
      error: () => {
        this.donneesSubject.next([]);
        this.chargementSubject.next(false);
      }
    });
  }

  obtenirLivreursDeMonZone(): Observable<any[]> {
    const url = `${environment.apiurl}users/livreurs/ma-zone`;
    return this.http.get<any[]>(url);
  }

  obtenirMembresDeMonZone(): Observable<any[]> {
    const url = `${environment.apiurl}users/ma-zone/membres`;
    return this.http.get<any[]>(url);
  }

  obtenirTypesUtilisateurs(){
    const url = `${environment.loginurl}` + 'type-utilisateurs';
    return this.http.get(url);
  }

  obtenirTousUtilisateurs(): Observable<any[]> {
    const url = `${environment.apiurl}users`;
    return this.http.get<any>(url).pipe(
      map((donnees: any) => Array.isArray(donnees) ? donnees : (donnees?.content ?? [])),
      catchError(this.messageService.gererErreur)
    );
  }

  changerMotDePasse(idUtilisateur: number, motDePasse: string): Observable<any> {
    const url = `${environment.apiurl}users/${idUtilisateur}/change-password`;
    const payload = {
      newPassword: motDePasse,
      confirmPassword: motDePasse,
      currentPassword: ''
    };
    return this.http.put<any>(url, payload).pipe(
      catchError(this.messageService.gererErreur)
    );
  }

  uploadPieceContrat(idUtilisateur: number, file: File): Observable<any> {
    const url = `${environment.apiurl}users/${idUtilisateur}/contract/upload`;
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<any>(url, formData).pipe(
      catchError(this.messageService.gererErreur)
    );
  }

  obtenirMoyennesNotationsLivreurs(): Observable<any[]> {
    const url = `${environment.apiurl}commandes/evaluations/livreurs/moyennes`;
    return this.http.get<any[]>(url).pipe(
      catchError(this.messageService.gererErreur)
    );
  }

  /** Profil consolidé (véhicule + documents) — utilisé par la page détail livreur. */
  obtenirProfilLivreurComplet(id: number): Observable<any> {
    const url = `${environment.apiurl}livreurs/${id}/profil-complet`;
    return this.http.get<any>(url).pipe(
      catchError(this.messageService.gererErreur)
    );
  }

  /** Stats agrégées (palier, courses, gains, note moyenne) — alignées sur l'écran profil mobile. */
  obtenirStatsLivreur(id: number): Observable<any> {
    const url = `${environment.apiurl}livreurs/${id}/stats`;
    return this.http.get<any>(url).pipe(
      catchError(this.messageService.gererErreur)
    );
  }

  // ── Gestion des véhicules livreur ────────────────────────────────────────

  creerVehicule(data: any): Observable<any> {
    const url = `${environment.apiurl}vehicules`;
    return this.http.post<any>(url, data).pipe(
      catchError(this.messageService.gererErreur)
    );
  }

  modifierVehicule(vehiculeId: number, data: any): Observable<any> {
    const url = `${environment.apiurl}vehicules/${vehiculeId}`;
    return this.http.put<any>(url, data).pipe(
      catchError(this.messageService.gererErreur)
    );
  }

  assignerVehiculeAuLivreur(livreurId: number, vehiculeId: number): Observable<any> {
    const url = `${environment.apiurl}livreurs/${livreurId}/vehicule/${vehiculeId}`;
    return this.http.put<any>(url, {}).pipe(
      catchError(this.messageService.gererErreur)
    );
  }

  desassignerVehicule(livreurId: number): Observable<any> {
    const url = `${environment.apiurl}livreurs/${livreurId}/vehicule`;
    return this.http.delete<any>(url).pipe(
      catchError(this.messageService.gererErreur)
    );
  }

  // ── Gestion des documents livreur ────────────────────────────────────────

  creerDocumentLivreur(data: any): Observable<any> {
    const url = `${environment.apiurl}documents-livreur`;
    return this.http.post<any>(url, data).pipe(
      catchError(this.messageService.gererErreur)
    );
  }

  modifierStatutDocument(docId: number, statut: string, commentaire?: string): Observable<any> {
    let url = `${environment.apiurl}documents-livreur/${docId}/statut?statut=${statut}`;
    if (commentaire) url += `&commentaire=${encodeURIComponent(commentaire)}`;
    return this.http.patch<any>(url, {}).pipe(
      catchError(this.messageService.gererErreur)
    );
  }

  supprimerDocumentLivreur(docId: number): Observable<any> {
    const url = `${environment.apiurl}documents-livreur/${docId}`;
    return this.http.delete<any>(url).pipe(
      catchError(this.messageService.gererErreur)
    );
  }

}
